import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { BiFilter } from 'react-icons/bi'

import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@/_components/ui'
import { cn } from '@/_lib/utils'
import { useSearchParams } from '@/features/_hooks/use-search-params'

interface QuickAction {
  label: string
  values: string[]
}

interface DataTableFilterProps {
  title: string
  placeholder: string
  filterKey: string
  triggerClassName?: string
  quickActions?: QuickAction[]
  disabled?: boolean
  options: {
    label: string
    value: string
  }[]
}

export function DataTableFilter({
  title,
  filterKey,
  placeholder,
  options,
  quickActions,
  triggerClassName,
  disabled,
}: DataTableFilterProps) {
  const [selectedValues, setSelectedValues] = useState<string[]>([])
  const { searchParams, setSearchParam } = useSearchParams()

  const pushFilterToUrl = (filters: string[]) => {
    setSearchParam(filterKey, filters.length ? filters.join(',') : '')
  }

  const filterValue = (searchParams as Record<string, unknown>)?.[filterKey] as string | undefined

  useEffect(() => {
    if (filterValue) {
      setSelectedValues(filterValue.split(','))
    } else {
      setSelectedValues([])
    }
  }, [filterValue])

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('h-9 font-medium', triggerClassName)}
          disabled={disabled}
        >
          <BiFilter className="mr-2 text-muted-foreground" size={18} />
          {title}
          {selectedValues?.length > 0 &&
            (() => {
              const matchedAction = quickActions?.find(
                (a) =>
                  a.values.length === selectedValues.length &&
                  a.values.every((v) => selectedValues.includes(v)),
              )
              return (
                <>
                  <Separator orientation="vertical" className="mx-2 h-4" />
                  <Badge className="rounded-sm bg-transparent px-1 font-bold text-brand-green-primary-5 hover:bg-transparent">
                    {matchedAction ? matchedAction.label : selectedValues.length}
                  </Badge>
                </>
              )
            })()}
          <ChevronDownIcon className="ml-1 h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder={placeholder} className="h-9" />
          <CommandList className="scrollbar">
            <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
            {quickActions && quickActions.length > 0 && (
              <>
                <CommandGroup>
                  {quickActions.map((action) => {
                    const isActive =
                      action.values.length === selectedValues.length &&
                      action.values.every((v) => selectedValues.includes(v))
                    return (
                      <CommandItem
                        key={action.label}
                        onSelect={() => {
                          const next = isActive ? [] : action.values
                          pushFilterToUrl(next)
                          setSelectedValues(next)
                        }}
                      >
                        <span className={cn('text-xs', isActive && 'font-semibold')}>
                          {action.label}
                        </span>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.includes(option.value)
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      const updatedValues = isSelected
                        ? selectedValues.filter((value) => value !== option.value)
                        : [...selectedValues, option.value]
                      pushFilterToUrl(updatedValues)
                      setSelectedValues(updatedValues)
                    }}
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-muted-foreground/40 [&_svg]:invisible',
                      )}
                    >
                      <CheckIcon className={cn('h-4 w-4')} />
                    </div>

                    <span>{option.label}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
          {selectedValues.length > 0 && (
            <>
              <CommandSeparator />
              <div className="flex justify-end p-2">
                <button
                  onClick={() => {
                    pushFilterToUrl([])
                    setSelectedValues([])
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Limpar filtros
                </button>
              </div>
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
